import { useEffect, useMemo, useState } from 'react';
import { FaBuilding, FaCheckCircle, FaChevronDown, FaExclamationCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance2 from '../../../api/axiosInstance2';
import { selectUserData, setTargetCompany } from '../../../reducers/user.reducer';
import companyService from '../../../services/companyService';
import SettingsCard from './SettingsCard';
import { getCompanyBaseURL, getCompanyToken, getCurrentCompanyInfo } from '../../../utils/companySession';

const getCompanyKey = (company, index) =>
  company?._id ?? company?.id ?? company?.companyId ?? company?.baseURL ?? `company-${index}`;

const getCompanyLabel = (company) =>
  company?.companyName ?? company?.name ?? company?.companyId ?? company?.baseURL ?? 'Unnamed company';

const findCompanyByKey = (companies, key) =>
  companies.find((company, index) => String(getCompanyKey(company, index)) === key);

const CompanySection = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserData);
  const currentCompanyInfo = useMemo(() => getCurrentCompanyInfo(userInfo), [userInfo]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const selectedCompany = useMemo(
    () => findCompanyByKey(companies, selectedCompanyKey),
    [companies, selectedCompanyKey],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchCompanies = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        const response = await companyService.getCompanies();
        const nextCompanies = Array.isArray(response?.data?.data) ? response.data.data : [];

        if (!isMounted) return;

        setCompanies(nextCompanies);
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error?.response?.data?.message || error?.message || 'Unable to load company list.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCompanies();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!companies.length || !currentCompanyInfo) return;

    const storedToken = getCompanyToken(currentCompanyInfo);
    const storedBaseURL = getCompanyBaseURL(currentCompanyInfo);
    const storedCompany = companies.find((company) => (
      (storedBaseURL && company?.baseURL === storedBaseURL) ||
      (storedToken && getCompanyToken(company) === storedToken)
    ));

    if (!storedCompany) return;

    const storedIndex = companies.indexOf(storedCompany);
    const nextCompanyKey = String(getCompanyKey(storedCompany, storedIndex));

    setSelectedCompanyKey((previousKey) => (
      previousKey === nextCompanyKey ? previousKey : nextCompanyKey
    ));
  }, [companies, currentCompanyInfo]);

  const handleCompanyChange = (event) => {
    const nextCompanyKey = event.target.value;
    const nextCompany = findCompanyByKey(companies, nextCompanyKey);

    setSelectedCompanyKey(nextCompanyKey);
    setSavedMessage('');

    if (!nextCompany) return;

    const baseURL = getCompanyBaseURL(nextCompany);
    const validCompanyToken = getCompanyToken(nextCompany);

    dispatch(setTargetCompany({ baseURL, validCompanyToken, companyInfo: nextCompany }));

    if (baseURL) {
      axiosInstance2.defaults.baseURL = baseURL;
    }
    if (validCompanyToken) {
      axiosInstance2.defaults.headers.common.Authorization = `Bearer ${validCompanyToken}`;
    }

    setSavedMessage(`${getCompanyLabel(nextCompany)} is active for this session.`);
  };

  return (
    <SettingsCard
      title="Company"
      description="Choose the company endpoint used by this browser session."
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="space-y-2">
          <label
            htmlFor="target-company"
            className="flex items-center gap-2 text-sm font-semibold dark:text-customHeading text-background"
          >
            <FaBuilding className="h-4 w-4 text-customAccent" aria-hidden="true" />
            Target Company
          </label>

          <div className="relative">
            <select
              id="target-company"
              value={selectedCompanyKey}
              onChange={handleCompanyChange}
              disabled={isLoading || companies.length === 0}
              className="h-12 w-full appearance-none rounded-lg border border-customBorder/60 dark:bg-custom_dark_background bg-customLightMilkWhite px-4 pr-10 text-sm font-semibold dark:text-customHeading text-background shadow-sm outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <option value="">
                {isLoading ? 'Loading companies...' : 'Select company'}
              </option>
              {companies.map((company, index) => {
                const companyKey = String(getCompanyKey(company, index));
                return (
                  <option key={companyKey} value={companyKey}>
                    {getCompanyLabel(company)}
                    {company?.companyId ? ` (${company.companyId})` : ''}
                  </option>
                );
              })}
            </select>
            <FaChevronDown
              className="pointer-events-none absolute right-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 dark:text-customMuted text-primary"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="min-h-12 rounded-lg border border-customBorder/40 dark:bg-custom_dark_background/40 bg-customLightMilkWhite px-4 py-3 text-sm">
          {errorMessage ? (
            <div className="flex items-center gap-2 font-medium text-red-500">
              <FaExclamationCircle className="h-4 w-4" aria-hidden="true" />
              {errorMessage}
            </div>
          ) : savedMessage ? (
            <div className="flex items-center gap-2 font-medium text-emerald-500">
              <FaCheckCircle className="h-4 w-4" aria-hidden="true" />
              {savedMessage}
            </div>
          ) : (
            <span className="dark:text-customMuted text-primary">
              {selectedCompany?.baseURL || 'No target company selected.'}
            </span>
          )}
        </div>
      </div>
    </SettingsCard>
  );
};

export default CompanySection;
